import type { AnalysisContext, ChatMessage, MapData, Transcript } from "../types";

const DEFAULT_TIMEOUT_MS = 270_000;
const CHAT_TIMEOUT_MS = 90_000;
const AVATAR_TIMEOUT_MS = 60_000;

export type AnalysisStatus = {
  step: number;
  message: string;
};

function withTimeout(
  ms: number,
  external?: AbortSignal,
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new TimeoutError(ms)), ms);
  let externalListener: (() => void) | null = null;
  if (external) {
    if (external.aborted) controller.abort(external.reason);
    else {
      externalListener = () => controller.abort(external.reason);
      external.addEventListener("abort", externalListener, { once: true });
    }
  }
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timer);
      if (external && externalListener) {
        external.removeEventListener("abort", externalListener);
      }
    },
  };
}

export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`Request timed out after ${Math.round(ms / 1000)}s`);
    this.name = "TimeoutError";
  }
}

export class CancelledError extends Error {
  constructor() {
    super("Request was cancelled");
    this.name = "CancelledError";
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

export interface AnalyzePayload {
  transcripts?: Transcript[];
  pdfs?: { name: string; base64: string }[];
  context: AnalysisContext;
}

export async function analyzeTranscripts(
  payload: AnalyzePayload,
  onStatus?: (status: AnalysisStatus) => void,
  signal?: AbortSignal,
): Promise<MapData> {
  const { signal: timeoutSignal, cleanup } = withTimeout(DEFAULT_TIMEOUT_MS, signal);

  try {
    const response = await fetch("/api/analyze", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(payload),
      signal: timeoutSignal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Analyze failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let result: MapData | null = null;
    let serverError: string | null = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split("\n\n");
      buffer = events.pop() ?? "";

      for (const raw of events) {
        if (!raw) continue;
        const eventLine = raw
          .split("\n")
          .find((l) => l.startsWith("event:"))
          ?.slice(6)
          .trim();
        const dataLine = raw
          .split("\n")
          .find((l) => l.startsWith("data:"))
          ?.slice(5)
          .trim();
        if (!eventLine || !dataLine) continue;

        let parsed: unknown;
        try {
          parsed = JSON.parse(dataLine);
        } catch {
          continue;
        }

        if (eventLine === "status") {
          onStatus?.(parsed as AnalysisStatus);
        } else if (eventLine === "result") {
          result = parsed as MapData;
        } else if (eventLine === "error") {
          serverError =
            (parsed as { message?: string }).message ?? "Unknown server error";
        }
      }
    }

    if (serverError) throw new Error(serverError);
    if (!result) throw new Error("Analysis ended without result");
    return result;
  } catch (error) {
    if (signal?.aborted) throw new CancelledError();
    if (timeoutSignal.aborted && timeoutSignal.reason instanceof TimeoutError) {
      throw timeoutSignal.reason;
    }
    if (isAbortError(error)) throw new CancelledError();
    throw error;
  } finally {
    cleanup();
  }
}

export async function chatWithSherlock(
  message: string,
  history: ChatMessage[],
  context: MapData,
  onToken?: (chunk: string) => void,
  signal?: AbortSignal,
): Promise<string> {
  const { signal: timeoutSignal, cleanup } = withTimeout(CHAT_TIMEOUT_MS, signal);
  try {
    const response = await fetch("/api/chat", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, history, context }),
      signal: timeoutSignal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Chat failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let full = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      full += chunk;
      onToken?.(chunk);
    }
    return full;
  } catch (error) {
    if (signal?.aborted) throw new CancelledError();
    if (isAbortError(error)) throw new CancelledError();
    throw error;
  } finally {
    cleanup();
  }
}

export async function generateAvatar(
  role: string,
  signal?: AbortSignal,
): Promise<string> {
  const { signal: timeoutSignal, cleanup } = withTimeout(AVATAR_TIMEOUT_MS, signal);
  try {
    const response = await fetch("/api/avatar", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
      signal: timeoutSignal,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Avatar generation failed (${response.status})`);
    }
    const data = (await response.json()) as { dataUrl: string; url?: string };
    return data.url ?? data.dataUrl;
  } finally {
    cleanup();
  }
}

export async function sendFeedback(
  rating: "up" | "down",
  payload: {
    confidenceScore?: number;
    speakerCount?: number;
    note?: string;
  },
): Promise<void> {
  try {
    await fetch("/api/feedback", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating, ...payload }),
      keepalive: true,
    });
  } catch {
    // Feedback is best-effort; never block the UI.
  }
}
